// Dr.Rx™ Clinical Consultation History & Pinned Sessions Sidebar
import React, { useState, useMemo } from 'react';
import type { ChatSession } from '../types';

const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
    </svg>
);
const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
);
const TrashIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
);
const PencilIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L15.232 5.232z" />
    </svg>
);
const PinIcon: React.FC<{ filled?: boolean }> = ({ filled }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${filled ? 'text-yellow-400 fill-current' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
    </svg>
);
const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
);

interface ChatSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    sessions: ChatSession[];
    activeSessionId: string | null;
    onSelectSession: (id: string) => void;
    onNewChat: () => void;
    onDeleteSession: (id: string) => void;
    onRenameSession: (id: string, newTitle: string) => void;
    onTogglePinSession?: (id: string) => void;
}

const ChatSidebar: React.FC<ChatSidebarProps> = ({
    isOpen,
    onClose,
    sessions,
    activeSessionId,
    onSelectSession,
    onNewChat,
    onDeleteSession,
    onRenameSession,
    onTogglePinSession,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [renamingId, setRenamingId] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState('');

    const handleRenameStart = (session: ChatSession, e: React.MouseEvent) => {
        e.stopPropagation();
        setRenamingId(session.id);
        setRenameValue(session.title);
    };

    const handleRenameSubmit = (sessionId: string) => {
        if (renameValue.trim()) {
            onRenameSession(sessionId, renameValue.trim());
        }
        setRenamingId(null);
    };

    const filteredSessions = useMemo(() => {
        const lower = searchTerm.trim().toLowerCase();
        return sessions
            .filter(session => {
                if (!lower) return true;
                const titleMatch = session.title.toLowerCase().includes(lower);
                const msgMatch = session.messages.some(m => m.text.toLowerCase().includes(lower));
                return titleMatch || msgMatch;
            })
            .sort((a, b) => b.createdAt - a.createdAt);
    }, [sessions, searchTerm]);

    const pinnedSessions = useMemo(
        () => filteredSessions.filter(s => s.isPinned),
        [filteredSessions]
    );

    const groupedSessions = useMemo(() => {
        const unpinned = filteredSessions.filter(s => !s.isPinned);
        const groups: Record<string, ChatSession[]> = {
            Today: [],
            Yesterday: [],
            'Previous 7 Days': [],
            Older: [],
        };
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

        unpinned.forEach(session => {
            const sessionDate = new Date(session.createdAt);
            if (sessionDate >= today) {
                groups.Today.push(session);
            } else if (sessionDate >= yesterday) {
                groups.Yesterday.push(session);
            } else if (sessionDate >= sevenDaysAgo) {
                groups['Previous 7 Days'].push(session);
            } else {
                groups.Older.push(session);
            }
        });

        return groups;
    }, [filteredSessions]);

    const getMatchSnippet = (session: ChatSession): string | null => {
        const lower = searchTerm.trim().toLowerCase();
        if (!lower || session.title.toLowerCase().includes(lower)) return null;
        const matchedMsg = session.messages.find(m => m.text.toLowerCase().includes(lower));
        if (!matchedMsg) return null;
        const cleanText = matchedMsg.text.replace(/###\s+/g, '').replace(/\*\*/g, '');
        const idx = cleanText.toLowerCase().indexOf(lower);
        const start = Math.max(0, idx - 24);
        const end = Math.min(cleanText.length, idx + lower.length + 40);
        return (start > 0 ? '…' : '') + cleanText.slice(start, end) + (end < cleanText.length ? '…' : '');
    };

    const renderSessionRow = (session: ChatSession) => {
        const snippet = getMatchSnippet(session);
        const isActive = activeSessionId === session.id;
        return (
            <li key={session.id}>
                <div
                    onClick={() => onSelectSession(session.id)}
                    className={`group flex flex-col p-2.5 rounded-xl text-sm font-medium cursor-pointer transition-all border ${
                        isActive
                            ? 'bg-blue-900/70 text-white border-yellow-400/50 shadow-md'
                            : 'text-gray-300 border-transparent hover:bg-white/5 hover:border-blue-900/40'
                    }`}
                >
                    <div className="flex items-center justify-between gap-2">
                        {renamingId === session.id ? (
                            <input
                                type="text"
                                value={renameValue}
                                onChange={e => setRenameValue(e.target.value)}
                                onBlur={() => handleRenameSubmit(session.id)}
                                onKeyDown={e => e.key === 'Enter' && handleRenameSubmit(session.id)}
                                className="w-full bg-transparent focus:outline-none focus:ring-1 focus:ring-yellow-400 rounded px-1 -mx-1 text-white"
                                autoFocus
                                onClick={e => e.stopPropagation()}
                            />
                        ) : (
                            <div className="flex items-center gap-2 min-w-0 flex-grow">
                                {session.isPinned && (
                                    <span className="text-yellow-400 flex-shrink-0" title="Pinned consultation">
                                        <PinIcon filled />
                                    </span>
                                )}
                                <span className={`truncate ${isActive ? 'font-bold text-yellow-300' : ''}`}>
                                    {session.title}
                                </span>
                            </div>
                        )}
                        {renamingId !== session.id && (
                            <div className="flex-shrink-0 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                {onTogglePinSession && (
                                    <button
                                        onClick={e => {
                                            e.stopPropagation();
                                            onTogglePinSession(session.id);
                                        }}
                                        className={`p-1.5 rounded-lg hover:bg-white/10 transition-colors ${
                                            session.isPinned ? 'text-yellow-400' : 'text-gray-400 hover:text-yellow-300'
                                        }`}
                                        aria-label={session.isPinned ? 'Unpin consultation' : 'Pin consultation'}
                                        title={session.isPinned ? 'Unpin' : 'Pin to top'}
                                    >
                                        <PinIcon filled={session.isPinned} />
                                    </button>
                                )}
                                <button
                                    onClick={e => handleRenameStart(session, e)}
                                    className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
                                    aria-label="Rename consultation"
                                    title="Rename"
                                >
                                    <PencilIcon />
                                </button>
                                <button
                                    onClick={e => {
                                        e.stopPropagation();
                                        onDeleteSession(session.id);
                                    }}
                                    className="p-1.5 text-gray-400 hover:text-red-400 rounded-lg hover:bg-red-500/10 transition-colors"
                                    aria-label="Delete consultation"
                                    title="Delete"
                                >
                                    <TrashIcon />
                                </button>
                            </div>
                        )}
                    </div>
                    {snippet && (
                        <p className="text-[11px] text-gray-400 mt-1 line-clamp-1 font-normal italic pl-1 border-l-2 border-yellow-400/50">
                            {snippet}
                        </p>
                    )}
                </div>
            </li>
        );
    };

    const sidebarContent = (
        <div className="bg-[#111E36] h-full w-80 flex flex-col p-4 border-r border-blue-900/50 text-white shadow-2xl">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-blue-900/50">
                <div>
                    <h2 className="text-lg font-bold text-white tracking-tight">Clinical Consultations</h2>
                    <p className="text-[11px] text-gray-400 tabular-nums">{sessions.length} saved threads</p>
                </div>
                <button
                    onClick={onClose}
                    className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
                    aria-label="Close sidebar"
                >
                    <CloseIcon />
                </button>
            </div>

            <button
                onClick={onNewChat}
                className="w-full flex items-center justify-center gap-2 bg-yellow-400 text-gray-950 font-bold py-2.5 px-4 rounded-xl hover:bg-yellow-300 transition-all shadow-md mb-4"
            >
                <PlusIcon /> New Clinical Consult
            </button>

            <div className="relative mb-4">
                <input
                    type="text"
                    placeholder="Search titles & clinical notes..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="w-full bg-[#0B1426] border border-blue-900/60 rounded-xl p-2.5 pl-9 pr-8 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-yellow-400"
                />
                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <SearchIcon />
                </div>
                {searchTerm && (
                    <button
                        onClick={() => setSearchTerm('')}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-white"
                        aria-label="Clear search"
                    >
                        ✕
                    </button>
                )}
            </div>

            <div className="flex-grow overflow-y-auto -mr-2 pr-2 space-y-5 custom-scrollbar">
                {pinnedSessions.length > 0 && (
                    <div>
                        <h3 className="text-[11px] font-bold text-yellow-400 uppercase tracking-wider px-2 mb-2 flex items-center gap-1.5">
                            <PinIcon filled /> Pinned Consultations
                        </h3>
                        <ul className="space-y-1">{pinnedSessions.map(renderSessionRow)}</ul>
                    </div>
                )}

                {Object.entries(groupedSessions).map(([groupTitle, groupSessions]) =>
                    groupSessions.length > 0 ? (
                        <div key={groupTitle}>
                            <h3 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider px-2 mb-2">
                                {groupTitle}
                            </h3>
                            <ul className="space-y-1">{groupSessions.map(renderSessionRow)}</ul>
                        </div>
                    ) : null
                )}

                {filteredSessions.length === 0 && (
                    <div className="text-center py-12 px-4">
                        <p className="text-sm font-semibold text-gray-400">No consultations found</p>
                        <p className="text-xs text-gray-500 mt-1">
                            {searchTerm
                                ? 'Try searching for another drug name, guideline, or clinical topic.'
                                : 'Start a new clinical consultation above.'}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <>
            {/* Overlay for all screen sizes */}
            <div
                onClick={onClose}
                className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-30 transition-opacity ${
                    isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
            />
            {/* Sidebar Drawer */}
            <aside
                className={`fixed top-0 left-0 h-full z-40 transform transition-transform duration-300 ease-out ${
                    isOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                {sidebarContent}
            </aside>
        </>
    );
};

export default ChatSidebar;